import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import openingAsset from "@/assets/mag-scroll/opening.webp.asset.json";
import posterAsset from "@/assets/mag-scroll/poster.webp.asset.json";
import storyAsset from "@/assets/mag-scroll/story.mp4.asset.json";
import { useMagScrollVideo } from "./useMagScrollVideo";
import { useGallerySwapMotion } from "./useGallerySwapMotion";
import "./mag-scroll-gallery.css";

export type MagGalleryImage = { src: string; alt: string };
export type MagScrollGalleryProps = {
  images?: MagGalleryImage[];
  className?: string;
  scrollScreens?: number;
  bottomInset?: number;
  onSlideChange?: (index: number) => void;
  galleryActions?: ReactNode;
};

const useClientLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

export function MagScrollGallery({
  images = [],
  className = "",
  scrollScreens = 2,
  bottomInset,
  onSlideChange,
  galleryActions,
}: MagScrollGalleryProps) {
  const galleryStyle = {
    "--mag-scroll-screens": Math.max(2, scrollScreens),
    ...(bottomInset == null ? {} : { "--mag-gallery-bottom-inset": `${Math.max(0, bottomInset)}px` }),
  } as CSSProperties;
  const [selected, setActive] = useState(0);
  const active = Math.min(selected, images.length);
  const sectionRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const pointerStart = useRef<{ x: number; y: number } | null>(null);
  const alignAfterChange = useRef(false);
  const prepareSwap = useGallerySwapMotion(mediaRef, active);
  const { animated, nearby, ready, viewportInsideRef } = useMagScrollVideo({
    sectionRef,
    stageRef,
    mediaRef,
    videoRef,
    enabled: active === 0,
  });
  const wasAnimated = useRef(false);
  const count = images.length + 1;
  const isStory = active === 0 || images.length === 0;
  const selectedImage = images[Math.min(active - 1, images.length - 1)];

  const select = (index: number) => {
    const next = (index + count) % count;
    if (next === active) return;
    prepareSwap(index - active);
    const rect = sectionRef.current?.getBoundingClientRect();
    alignAfterChange.current = Boolean(rect && rect.top <= 81 && rect.bottom > 0);
    setActive(next);
    onSlideChange?.(next);
  };

  useClientLayoutEffect(() => {
    const collapsedInView = wasAnimated.current && !animated && viewportInsideRef.current;
    if (alignAfterChange.current || collapsedInView) {
      sectionRef.current?.scrollIntoView({ behavior: "instant", block: "start" });
    }
    alignAfterChange.current = false;
    wasAnimated.current = animated;
  }, [active, animated, viewportInsideRef]);

  return (
    <div
      ref={sectionRef}
      className={["mag-scroll-gallery", className].filter(Boolean).join(" ")}
      style={galleryStyle}
      data-motion={animated}
      data-ready={ready && animated}
      data-story={isStory}
      data-engagement-section="mag-story"
    >
      <div ref={stageRef} className="mag-gallery-stage">
        <div
          ref={mediaRef}
          className="mag-gallery-visual"
          role="region"
          aria-roledescription="carousel"
          aria-label="Mag Absorb Pro product gallery"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
              event.preventDefault();
              select(active + (event.key === "ArrowRight" ? 1 : -1));
            }
          }}
          onPointerDown={(event) => {
            pointerStart.current = { x: event.clientX, y: event.clientY };
          }}
          onPointerCancel={() => {
            pointerStart.current = null;
          }}
          onPointerUp={(event) => {
            const start = pointerStart.current;
            pointerStart.current = null;
            if (!start || (event.target as Element).closest("button,a")) return;
            const dx = event.clientX - start.x;
            const dy = event.clientY - start.y;
            if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.3) {
              select(active + (dx < 0 ? 1 : -1));
            }
          }}
        >
          <img
            className="mag-gallery-poster"
            src={isStory ? (animated ? openingAsset.url : posterAsset.url) : selectedImage?.src}
            width={1080}
            height={1080}
            alt={
              isStory
                ? animated
                  ? "Make space to switch off. Introducing Mag Absorb Pro."
                  : "Mag Absorb Pro bottle in a white studio."
                : (selectedImage?.alt ?? "Mag Absorb Pro")
            }
            loading={isStory ? "eager" : "lazy"}
            decoding="async"
            draggable={false}
          />
          {animated && nearby ? (
            <video
              ref={videoRef}
              className="mag-gallery-video"
              width={1080}
              height={1080}
              muted
              playsInline
              preload="auto"
              poster={openingAsset.url}
              disablePictureInPicture
              tabIndex={-1}
              aria-hidden="true"
            >
              <source src={storyAsset.url} type="video/mp4" />
            </video>
          ) : null}
          {galleryActions ? <div className="mag-gallery-actions">{galleryActions}</div> : null}
          {animated ? <span className="mag-gallery-progress" aria-hidden="true"><span /></span> : null}
        </div>
        <div className="mag-gallery-thumbnails" aria-label="Product images">
          <button type="button" aria-label="Show Mag Absorb Pro story" aria-pressed={active === 0} onClick={() => select(0)}>
            <img src={posterAsset.url} alt="" width={56} height={56} loading="lazy" decoding="async" />
          </button>
          {images.map((image, index) => (
            <button key={`${image.src}-${index}`} type="button" aria-label={`Show ${image.alt}`} aria-pressed={active === index + 1} onClick={() => select(index + 1)}>
              <img src={image.src} alt="" width={56} height={56} loading="lazy" decoding="async" />
            </button>
          ))}
        </div>

        <p className="mag-gallery-transcript">
          Make space to switch off. Meet Mag Absorb Pro. 5 magnesium forms. One formula. Sleep and recovery support. Start your nightly reset. With Mag Absorb Pro. Food supplement. Not a medicine.
        </p>
      </div>
    </div>
  );
}
